import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'tailwind-components/Modal/Modal';
import Button from 'tailwind-components/Button/Button';
import RadioFieldset from 'tailwind-components/RadioFieldset/RadioFieldset';
import { Mail, Smartphone } from 'lucide-react';
import { useDispatch } from 'react-redux';
import { showAlert } from 'state/actions/site';
import {
  rateQuoteApplyQuestions as Q,
  rateQuoteApplyStartOptions,
  rateQuoteApplyTransfersOptions,
  rateQuoteDetail
} from '../data/rateQuoteStaticContent';
import useLeadPartnerApplySubmit from '../hooks/useLeadPartnerApplySubmit';

const emptyForm = {
  agencyName: '',
  primaryContactName: '',
  email: '',
  phone: '',
  activeAgentsCount: '',
  writingAcaBusiness: '',
  inboundCallsAvailable: '',
  transfersPerDay: '',
  startTimeline: '',
  notes: ''
};

const yesNoOptions = [
  { value: 'Yes', label: 'Yes' },
  { value: 'No', label: 'No' }
];

const inputClass =
  'w-full rounded-lg border border-grey-lighter bg-white px-3 py-2 text-black text-[length:--font-size-body] outline-none focus:border-primary-emphasis';

const labelClass = 'block text-sm font-semibold text-black mb-1.5';

function Req() {
  return <span className='text-semantic-danger'> *</span>;
}

function buildQuestionsPayload(form) {
  const rows = [
    { question: Q.agencyName, answer: form.agencyName.trim() },
    {
      question: Q.primaryContactName,
      answer: form.primaryContactName.trim()
    },
    { question: Q.email, answer: form.email.trim() },
    { question: Q.phone, answer: form.phone.trim() },
    {
      question: Q.activeAgentsCount,
      answer: form.activeAgentsCount.trim()
    },
    {
      question: Q.writingAcaBusiness,
      answer: form.writingAcaBusiness
    },
    {
      question: Q.inboundCallsAvailable,
      answer: form.inboundCallsAvailable
    },
    {
      question: Q.transfersPerDay,
      answer: form.transfersPerDay
    },
    {
      question: Q.startTimeline,
      answer: form.startTimeline
    }
  ];
  const notes = form.notes.trim();
  if (notes) {
    rows.push({ question: Q.notesOptional, answer: notes });
  }
  return rows;
}

function isFormValid(form) {
  return (
    form.agencyName.trim() &&
    form.primaryContactName.trim() &&
    form.email.trim() &&
    form.phone.trim() &&
    form.activeAgentsCount.trim() &&
    form.writingAcaBusiness &&
    form.inboundCallsAvailable &&
    form.transfersPerDay &&
    form.startTimeline
  );
}

export default function LeadPartnerApplyModal({
  active,
  closeModal,
  leadPartnerName
}) {
  const dispatch = useDispatch();
  const copy = rateQuoteDetail.modal;
  const { submitApplication, isSubmitting } = useLeadPartnerApplySubmit();
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    if (!active) return;
    setForm({ ...emptyForm });
  }, [active]);

  const setField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!isFormValid(form)) {
      dispatch(
        showAlert({
          message: 'Please complete all required fields.',
          code: 'error'
        })
      );
      return;
    }
    const questions = buildQuestionsPayload(form);
    const ok = await submitApplication({
      leadPartnerName,
      questions
    });
    if (ok) closeModal();
  };

  const modalTitle = (
    <>
      <span className='block text-xl font-semibold leading-tight'>
        {copy.title}
      </span>
      <span className='block text-sm font-normal opacity-95 mt-1'>
        {copy.subtitle}
      </span>
    </>
  );

  return (
    <Modal
      active={active}
      closeModal={closeModal}
      title={modalTitle}
      size='large'
      hideFooter
      headerClassName='-mx-5 -mt-5 rounded-t-[14px] px-5 pt-5 pb-4 bg-[linear-gradient(90deg,var(--color-primary-darkened),var(--color-primary-emphasis-light))]'
      headerTitleClassName='flex flex-col items-start text-left text-white [&_span]:text-white'
      closeButtonClassName='[&_svg]:size-[22px] -mr-1 -mt-1 shrink-0 text-white! hover:bg-white/15! hover:text-white! border-transparent focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-0'
      showScrollbar
    >
      <form onSubmit={handleSubmit} className='space-y-4 pb-2'>
        <div>
          <label className={labelClass} htmlFor='lp-agency'>
            {Q.agencyName}
            <Req />
          </label>
          <input
            id='lp-agency'
            className={inputClass}
            value={form.agencyName}
            onChange={e => setField('agencyName', e.target.value)}
            autoComplete='organization'
          />
        </div>

        <div>
          <label className={labelClass} htmlFor='lp-contact'>
            {Q.primaryContactName}
            <Req />
          </label>
          <input
            id='lp-contact'
            className={inputClass}
            value={form.primaryContactName}
            onChange={e => setField('primaryContactName', e.target.value)}
            autoComplete='name'
          />
        </div>

        <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
          <div>
            <label className={labelClass} htmlFor='lp-email'>
              {Q.email}
              <Req />
            </label>
            <div className='relative'>
              <Mail
                className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-grey'
                aria-hidden
              />
              <input
                id='lp-email'
                type='email'
                className={`${inputClass} pl-10`}
                value={form.email}
                onChange={e => setField('email', e.target.value)}
                autoComplete='email'
              />
            </div>
          </div>
          <div>
            <label className={labelClass} htmlFor='lp-phone'>
              {Q.phone}
              <Req />
            </label>
            <div className='relative'>
              <Smartphone
                className='pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-grey'
                aria-hidden
              />
              <input
                id='lp-phone'
                type='tel'
                className={`${inputClass} pl-10`}
                value={form.phone}
                onChange={e => setField('phone', e.target.value)}
                autoComplete='tel'
              />
            </div>
          </div>
        </div>

        <div className='w-full'>
          <label className={labelClass} htmlFor='lp-active-agents'>
            {Q.activeAgentsCount}
            <Req />
          </label>
          <input
            id='lp-active-agents'
            type='text'
            inputMode='numeric'
            pattern='[0-9]*'
            className={inputClass}
            value={form.activeAgentsCount}
            onChange={e =>
              setField('activeAgentsCount', e.target.value.replace(/\D/g, ''))
            }
          />
        </div>

        <RadioFieldset
          legend={Q.writingAcaBusiness}
          name='writingAcaBusiness'
          value={form.writingAcaBusiness}
          onChange={v => setField('writingAcaBusiness', v)}
          options={yesNoOptions}
        />

        <RadioFieldset
          legend={Q.inboundCallsAvailable}
          name='inboundCallsAvailable'
          value={form.inboundCallsAvailable}
          onChange={v => setField('inboundCallsAvailable', v)}
          options={yesNoOptions}
        />

        <RadioFieldset
          legend={Q.transfersPerDay}
          name='transfersPerDay'
          value={form.transfersPerDay}
          onChange={v => setField('transfersPerDay', v)}
          options={rateQuoteApplyTransfersOptions}
        />

        <RadioFieldset
          legend={Q.startTimeline}
          name='startTimeline'
          value={form.startTimeline}
          onChange={v => setField('startTimeline', v)}
          options={rateQuoteApplyStartOptions}
        />

        <div>
          <label className={labelClass} htmlFor='lp-notes'>
            {Q.notesOptional}
          </label>
          <textarea
            id='lp-notes'
            rows={4}
            className={`${inputClass} resize-y min-h-[100px]`}
            value={form.notes}
            onChange={e => setField('notes', e.target.value)}
          />
        </div>

        <div className='flex justify-center'>
          <Button
            type='submit'
            text={copy.submitLabel}
            variant='success'
            fill
            rounded='medium'
            isLoading={isSubmitting}
            isDisabled={isSubmitting}
            className='justify-center'
          />
        </div>
      </form>
    </Modal>
  );
}

LeadPartnerApplyModal.propTypes = {
  active: PropTypes.bool.isRequired,
  closeModal: PropTypes.func.isRequired,
  leadPartnerName: PropTypes.string.isRequired
};
